import { supabase } from '../lib/supabase';

export const jobSiteService = {
  // Get all job sites for a company
  async getCompanyJobSites(companyId, includeInactive = false) {
    let query = supabase
      ?.from('job_sites')
      ?.select(`
        *,
        client:clients(id, client_name, is_active)
      `)
      ?.eq('company_id', companyId)
      ?.order('site_name', { ascending: true });

    if (!includeInactive) {
      query = query?.eq('is_active', true);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(site => ({
      id: site?.id,
      companyId: site?.company_id,
      clientId: site?.client_id,
      siteName: site?.site_name,
      siteTag: site?.site_tag,
      address: site?.address,
      addressLat: site?.address_lat,
      addressLng: site?.address_lng,
      contactPerson: site?.contact_person,
      contactEmail: site?.contact_email,
      contactPhone: site?.contact_phone,
      notes: site?.notes,
      isActive: site?.is_active,
      createdAt: site?.created_at,
      updatedAt: site?.updated_at,
      client: site?.client ? {
        id: site?.client?.id,
        clientName: site?.client?.client_name,
        isActive: site?.client?.is_active
      } : null
    }));
  },

  // Get job sites for a specific client
  async getClientJobSites(clientId, includeInactive = false) {
    let query = supabase
      ?.from('job_sites')
      ?.select('*')
      ?.eq('client_id', clientId)
      ?.order('site_name', { ascending: true });

    if (!includeInactive) {
      query = query?.eq('is_active', true);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(site => ({
      id: site?.id,
      companyId: site?.company_id,
      clientId: site?.client_id,
      siteName: site?.site_name,
      siteTag: site?.site_tag,
      address: site?.address,
      addressLat: site?.address_lat,
      addressLng: site?.address_lng,
      contactPerson: site?.contact_person,
      contactEmail: site?.contact_email,
      contactPhone: site?.contact_phone,
      notes: site?.notes,
      isActive: site?.is_active,
      createdAt: site?.created_at,
      updatedAt: site?.updated_at
    }));
  },

  // Search job sites by name (across all clients for a company)
  async searchJobSites(companyId, searchTerm, clientId = null) {
    let query = supabase
      ?.from('job_sites')
      ?.select(`
        *,
        client:clients(id, client_name)
      `)
      ?.eq('company_id', companyId)
      ?.eq('is_active', true)
      ?.ilike('site_name', `%${searchTerm}%`)
      ?.order('site_name', { ascending: true })
      ?.limit(10);

    if (clientId) {
      query = query?.eq('client_id', clientId);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(site => ({
      id: site?.id,
      companyId: site?.company_id,
      clientId: site?.client_id,
      siteName: site?.site_name,
      siteTag: site?.site_tag,
      address: site?.address,
      addressLat: site?.address_lat,
      addressLng: site?.address_lng,
      contactPerson: site?.contact_person,
      contactEmail: site?.contact_email,
      contactPhone: site?.contact_phone,
      notes: site?.notes,
      isActive: site?.is_active,
      client: site?.client ? {
        id: site?.client?.id,
        clientName: site?.client?.client_name
      } : null
    }));
  },

  // Get a single job site by ID
  async getJobSite(jobSiteId) {
    const { data, error } = await supabase
      ?.from('job_sites')
      ?.select(`
        *,
        client:clients(id, client_name, contact_person, contact_email, contact_phone, is_active)
      `)
      ?.eq('id', jobSiteId)
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      companyId: data?.company_id,
      clientId: data?.client_id,
      siteName: data?.site_name,
      siteTag: data?.site_tag,
      address: data?.address,
      addressLat: data?.address_lat,
      addressLng: data?.address_lng,
      contactPerson: data?.contact_person,
      contactEmail: data?.contact_email,
      contactPhone: data?.contact_phone,
      notes: data?.notes,
      isActive: data?.is_active,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at,
      client: data?.client ? {
        id: data?.client?.id,
        clientName: data?.client?.client_name,
        contactPerson: data?.client?.contact_person,
        contactEmail: data?.client?.contact_email,
        contactPhone: data?.client?.contact_phone,
        isActive: data?.client?.is_active
      } : null
    };
  },

  // Create a new job site
  async createJobSite(jobSiteData, companyId) {
    const { data, error } = await supabase
      ?.from('job_sites')
      ?.insert({
        company_id: companyId,
        client_id: jobSiteData?.clientId,
        site_name: jobSiteData?.siteName,
        site_tag: jobSiteData?.siteTag || null,
        address: jobSiteData?.address,
        address_lat: jobSiteData?.addressLat,
        address_lng: jobSiteData?.addressLng,
        contact_person: jobSiteData?.contactPerson,
        contact_email: jobSiteData?.contactEmail,
        contact_phone: jobSiteData?.contactPhone,
        notes: jobSiteData?.notes
      })
      ?.select(`
        *,
        client:clients(id, client_name)
      `)
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      companyId: data?.company_id,
      clientId: data?.client_id,
      siteName: data?.site_name,
      siteTag: data?.site_tag,
      address: data?.address,
      addressLat: data?.address_lat,
      addressLng: data?.address_lng,
      contactPerson: data?.contact_person,
      contactEmail: data?.contact_email,
      contactPhone: data?.contact_phone,
      notes: data?.notes,
      isActive: data?.is_active,
      createdAt: data?.created_at,
      client: data?.client ? {
        id: data?.client?.id,
        clientName: data?.client?.client_name
      } : null
    };
  },

  // Update a job site
  async updateJobSite(jobSiteId, updates) {
    const updateData = {};

    if (updates?.clientId !== undefined) updateData.client_id = updates.clientId;
    if (updates?.siteName !== undefined) updateData.site_name = updates.siteName;
    if (updates?.siteTag !== undefined) updateData.site_tag = updates.siteTag;
    if (updates?.address !== undefined) updateData.address = updates.address;
    if (updates?.addressLat !== undefined) updateData.address_lat = updates.addressLat;
    if (updates?.addressLng !== undefined) updateData.address_lng = updates.addressLng;
    if (updates?.contactPerson !== undefined) updateData.contact_person = updates.contactPerson;
    if (updates?.contactEmail !== undefined) updateData.contact_email = updates.contactEmail;
    if (updates?.contactPhone !== undefined) updateData.contact_phone = updates.contactPhone;
    if (updates?.notes !== undefined) updateData.notes = updates.notes;
    if (updates?.isActive !== undefined) updateData.is_active = updates.isActive;

    const { data, error } = await supabase
      ?.from('job_sites')
      ?.update(updateData)
      ?.eq('id', jobSiteId)
      ?.select(`
        *,
        client:clients(id, client_name)
      `)
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      companyId: data?.company_id,
      clientId: data?.client_id,
      siteName: data?.site_name,
      siteTag: data?.site_tag,
      address: data?.address,
      addressLat: data?.address_lat,
      addressLng: data?.address_lng,
      contactPerson: data?.contact_person,
      contactEmail: data?.contact_email,
      contactPhone: data?.contact_phone,
      notes: data?.notes,
      isActive: data?.is_active,
      updatedAt: data?.updated_at,
      client: data?.client ? {
        id: data?.client?.id,
        clientName: data?.client?.client_name
      } : null
    };
  },

  // Update job site address and optionally update related jobs
  async updateJobSiteAddress(jobSiteId, newAddress, addressLat, addressLng, updateJobsOption = 'future') {
    // First update the job site itself
    const { data: updatedSite, error: siteError } = await supabase
      ?.from('job_sites')
      ?.update({
        address: newAddress,
        address_lat: addressLat,
        address_lng: addressLng
      })
      ?.eq('id', jobSiteId)
      ?.select()
      ?.single();

    if (siteError) throw siteError;

    // Optionally update jobs based on user preference
    if (updateJobsOption === 'all') {
      // Update all jobs with this job site
      const { error: jobsError } = await supabase
        ?.from('jobs')
        ?.update({ original_address: newAddress })
        ?.eq('job_site_id', jobSiteId);

      if (jobsError) throw jobsError;
    } else if (updateJobsOption === 'active') {
      // Update only active jobs (not completed/cancelled)
      const { error: jobsError } = await supabase
        ?.from('jobs')
        ?.update({ original_address: newAddress })
        ?.eq('job_site_id', jobSiteId)
        ?.not('status', 'in', '("completed","cancelled")');

      if (jobsError) throw jobsError;
    }
    // 'future' means don't update existing jobs, only new ones will use the new address

    return {
      id: updatedSite?.id,
      companyId: updatedSite?.company_id,
      clientId: updatedSite?.client_id,
      siteName: updatedSite?.site_name,
      address: updatedSite?.address,
      addressLat: updatedSite?.address_lat,
      addressLng: updatedSite?.address_lng,
      contactPerson: updatedSite?.contact_person,
      contactEmail: updatedSite?.contact_email,
      contactPhone: updatedSite?.contact_phone,
      notes: updatedSite?.notes,
      isActive: updatedSite?.is_active,
      updatedAt: updatedSite?.updated_at
    };
  },

  // Deactivate a job site (soft delete)
  async deactivateJobSite(jobSiteId) {
    const { error } = await supabase
      ?.from('job_sites')
      ?.update({ is_active: false })
      ?.eq('id', jobSiteId);

    if (error) throw error;
  },

  // Reactivate a job site
  async reactivateJobSite(jobSiteId) {
    const { error } = await supabase
      ?.from('job_sites')
      ?.update({ is_active: true })
      ?.eq('id', jobSiteId);

    if (error) throw error;
  },

  // Delete a job site (hard delete - use with caution)
  async deleteJobSite(jobSiteId) {
    const { error } = await supabase
      ?.from('job_sites')
      ?.delete()
      ?.eq('id', jobSiteId);

    if (error) throw error;
  },

  // Get jobs for a job site
  async getJobSiteJobs(jobSiteId) {
    const { data, error } = await supabase
      ?.from('jobs')
      ?.select(`
        *,
        client:clients(id, client_name),
        created_by_profile:user_profiles!jobs_created_by_fkey(id, full_name, email)
      `)
      ?.eq('job_site_id', jobSiteId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(job => ({
      id: job?.id,
      jobDisplayId: job?.job_display_id,
      jobName: job?.job_name,
      description: job?.description,
      status: job?.status,
      priority: job?.priority,
      originalAddress: job?.original_address,
      client: job?.client ? {
        id: job?.client?.id,
        clientName: job?.client?.client_name
      } : null,
      createdAt: job?.created_at,
      createdByProfile: job?.created_by_profile
    }));
  },

  // Get job count for a job site
  async getJobSiteJobCount(jobSiteId) {
    const { count, error } = await supabase
      ?.from('jobs')
      ?.select('*', { count: 'exact', head: true })
      ?.eq('job_site_id', jobSiteId);

    if (error) throw error;

    return count || 0;
  },

  // Check if a job site has any active jobs
  async hasActiveJobs(jobSiteId) {
    const { count, error } = await supabase
      ?.from('jobs')
      ?.select('*', { count: 'exact', head: true })
      ?.eq('job_site_id', jobSiteId)
      ?.not('status', 'in', '("completed","cancelled")');

    if (error) throw error;

    return (count || 0) > 0;
  }
};
